import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Mic2, MicOff, CheckCircle } from 'lucide-react';

const NOTE_STRINGS = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const STANDARD_TUNING = [
  { string: 6, note: 'E', freq: 82.41 },
  { string: 5, note: 'A', freq: 110.00 },
  { string: 4, note: 'D', freq: 146.83 },
  { string: 3, note: 'G', freq: 196.00 },
  { string: 2, note: 'B', freq: 246.94 },
  { string: 1, note: 'E', freq: 329.63 },
];

const TunerPage = () => {
  const { t } = useTranslation();
  const [isListening, setIsListening] = useState(false);
  const [frequency, setFrequency] = useState(null);
  const [note, setNote] = useState('--');
  const [cents, setCents] = useState(0);
  const [octave, setOctave] = useState(0);
  const [selectedString, setSelectedString] = useState(null);
  
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const rafIdRef = useRef(null);

  const getNote = useCallback((freq) => {
    const noteNum = 12 * (Math.log(freq / 440) / Math.log(2));
    const roundedNote = Math.round(noteNum);
    const centsOff = Math.round((noteNum - roundedNote) * 100);
    const noteIndex = (roundedNote + 69) % 12;
    const octave = Math.floor((roundedNote + 69) / 12) - 1;
    
    return {
      note: NOTE_STRINGS[noteIndex],
      cents: centsOff,
      octave: octave
    };
  }, []);

  const autoCorrelate = useCallback((buffer, sampleRate) => {
    const SIZE = buffer.length;
    const sumOfSquares = buffer.reduce((sum, val) => sum + val * val, 0);
    const rootMeanSquare = Math.sqrt(sumOfSquares / SIZE);
    
    if (rootMeanSquare < 0.01) return -1;

    let r1 = 0, r2 = SIZE - 1;
    const threshold = 0.2;
    
    for (let i = 0; i < SIZE / 2; i++) {
      if (Math.abs(buffer[i]) < threshold) { r1 = i; break; }
    }
    for (let i = 1; i < SIZE / 2; i++) {
      if (Math.abs(buffer[SIZE - i]) < threshold) { r2 = SIZE - i; break; }
    }

    const buf2 = buffer.slice(r1, r2);
    const c = new Array(buf2.length).fill(0);
    
    for (let i = 0; i < buf2.length; i++) {
      for (let j = 0; j < buf2.length - i; j++) {
        c[i] += buf2[j] * buf2[j + i];
      }
    }

    let d = 0;
    while (c[d] > c[d + 1]) d++;
    
    let maxVal = -1, maxPos = -1;
    for (let i = d; i < buf2.length; i++) {
      if (c[i] > maxVal) {
        maxVal = c[i];
        maxPos = i;
      }
    }

    let T0 = maxPos;
    const x1 = c[T0 - 1], x2 = c[T0], x3 = c[T0 + 1];
    const a = (x1 + x3 - 2 * x2) / 2;
    const b = (x3 - x1) / 2;
    
    if (a) T0 = T0 - b / (2 * a);

    return sampleRate / T0;
  }, []);

  const updatePitch = useCallback(() => {
    if (!analyserRef.current) return;
    
    const buffer = new Float32Array(analyserRef.current.fftSize);
    analyserRef.current.getFloatTimeDomainData(buffer);
    
    const freq = autoCorrelate(buffer, audioContextRef.current.sampleRate);
    
    if (freq > 50 && freq < 1000) {
      setFrequency(Math.round(freq * 10) / 10);
      const { note, cents, octave } = getNote(freq);
      setNote(note);
      setCents(cents);
      setOctave(octave);
    }
    
    rafIdRef.current = requestAnimationFrame(updatePitch);
  }, [autoCorrelate, getNote]);

  const startListening = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 2048;
      
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      
      setIsListening(true);
      updatePitch();
    } catch (error) {
      console.error('Error accessing microphone:', error);
    }
  };

  const stopListening = () => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
    setIsListening(false);
    setFrequency(null);
    setNote('--');
    setCents(0);
  };

  useEffect(() => {
    return () => {
      stopListening();
    };
  }, []);

  const isInTune = cents >= -5 && cents <= 5;
  const needleRotation = Math.max(-45, Math.min(45, cents * 0.9));

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20 pb-12 px-6 md:px-12" data-testid="tuner-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12 text-center"
        >
          <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-red-500 to-rose-400 flex items-center justify-center mx-auto mb-4">
            <Mic2 className="w-8 h-8 text-black" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-2">
            {t('tuner.title')}
          </h1>
          <p className="text-zinc-400">{t('tuner.standardTuning')}</p>
        </motion.div>

        {/* Tuner Display */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-[#121212] border border-white/5 rounded-3xl p-8 mb-8"
        >
          {/* String Selector */}
          <div className="flex justify-center gap-3 mb-8">
            {STANDARD_TUNING.map((s) => (
              <button
                key={s.string}
                onClick={() => setSelectedString(selectedString === s.string ? null : s.string)}
                data-testid={`string-${s.string}`}
                className={`w-12 h-12 rounded-full font-heading font-bold text-lg transition-all ${
                  selectedString === s.string
                    ? 'bg-amber text-black scale-110'
                    : 'bg-white/5 text-zinc-400 hover:bg-white/10'
                }`}
              >
                {s.note}
              </button>
            ))}
          </div>

          {/* Meter */}
          <div className="relative h-48 flex items-center justify-center mb-8">
            {/* Meter Background */}
            <div className="absolute w-full max-w-md h-2 bg-gradient-to-r from-red-500 via-green-500 to-red-500 rounded-full opacity-30" />
            
            {/* Center Indicator */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 w-1 h-16 bg-green-500" />
            
            {/* Needle */}
            {isListening && frequency && (
              <motion.div
                className="absolute bottom-0 left-1/2 origin-bottom"
                style={{ 
                  transform: `translateX(-50%) rotate(${needleRotation}deg)`,
                  transition: 'transform 0.1s ease-out'
                }}
              >
                <div className={`w-1 h-32 rounded-full ${isInTune ? 'bg-green-500 shadow-[0_0_20px_rgba(16,185,129,0.5)]' : 'bg-amber shadow-[0_0_20px_rgba(255,159,28,0.5)]'}`} />
              </motion.div>
            )}
            
            {/* Note Display */}
            <div className="relative z-10 text-center">
              <div className={`font-heading text-8xl font-black ${isInTune && isListening ? 'text-green-400 glow-text-amber' : 'text-white'}`}>
                {note}
              </div>
              {octave > 0 && (
                <div className="text-zinc-500 text-lg">{octave}</div>
              )}
            </div>
          </div>

          {/* Frequency & Cents */}
          <div className="flex justify-center gap-8 mb-8">
            <div className="text-center">
              <p className="text-zinc-500 text-sm mb-1">{t('tuner.frequency')}</p>
              <p className="font-mono text-2xl text-white">
                {frequency ? `${frequency} Hz` : '-- Hz'}
              </p>
            </div>
            <div className="text-center">
              <p className="text-zinc-500 text-sm mb-1">{t('tuner.cents')}</p>
              <p className={`font-mono text-2xl ${isInTune ? 'text-green-400' : cents > 0 ? 'text-amber' : 'text-blue'}`}>
                {isListening ? (cents > 0 ? `+${cents}` : cents) : '--'}
              </p>
            </div>
          </div>

          {/* Status */}
          {isListening && (
            <div className="text-center mb-6">
              {isInTune ? (
                <div className="flex items-center justify-center gap-2 text-green-400">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-semibold">{t('tuner.inTune')}</span>
                </div>
              ) : (
                <div className={cents > 0 ? 'text-amber' : 'text-blue'}>
                  {cents > 0 ? t('tuner.tooHigh') : t('tuner.tooLow')}
                </div>
              )}
            </div>
          )}

          {/* Control Button */}
          <div className="flex justify-center">
            <Button
              onClick={isListening ? stopListening : startListening}
              data-testid="tuner-toggle-btn"
              size="lg"
              className={`px-12 py-6 rounded-full font-bold text-lg ${
                isListening
                  ? 'bg-red-500 hover:bg-red-600 text-white'
                  : 'bg-gradient-to-r from-amber to-orange-500 text-black hover:scale-105'
              } transition-all`}
            >
              {isListening ? (
                <>
                  <MicOff className="w-5 h-5 mr-2" />
                  {t('tuner.stop')}
                </>
              ) : (
                <>
                  <Mic2 className="w-5 h-5 mr-2" />
                  {t('tuner.start')}
                </>
              )}
            </Button>
          </div>
        </motion.div>

        {/* String Reference */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[#121212] border border-white/5 rounded-2xl p-6"
        >
          <h3 className="font-heading text-lg font-bold text-white mb-4">{t('tuner.reference')}</h3>
          <div className="grid grid-cols-6 gap-4">
            {STANDARD_TUNING.map((s) => (
              <div key={s.string} className="text-center">
                <div className="text-zinc-500 text-sm mb-1">{t('tuner.string')} {s.string}</div>
                <div className="font-heading font-bold text-white text-xl">{s.note}</div>
                <div className="font-mono text-zinc-500 text-sm">{s.freq} Hz</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TunerPage;
